import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Layers, Save, ToggleLeft, ToggleRight, Archive, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const STATUSES = ["published", "beta", "coming_soon", "maintenance", "hidden", "archived"];
const VISIBILITIES = ["everyone", "trial", "standard", "premium", "enterprise", "selected_users", "internal", "hidden"];

const STATUS_COLOR = {
  published: "text-[#34D399] border-[#34D399]/40",
  beta: "text-[#00E5FF] border-[#00E5FF]/40",
  coming_soon: "text-[#F472B6] border-[#F472B6]/40",
  maintenance: "text-[#F59E0B] border-[#F59E0B]/40",
  hidden: "text-white/40 border-white/20",
  archived: "text-white/30 border-white/10",
};

export default function AdminModules() {
  const { user } = useAuth();
  const [mods, setMods] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get("/admin/modules").then((r) => setMods(r.data.items || [])).catch(() => toast.error("Could not load modules"));
  };
  useEffect(load, []);

  if (user?.role !== "super_admin") {
    return <div className="p-8 text-white/50">Super admin access required.</div>;
  }

  const patch = async (key, body) => {
    try {
      await api.patch(`/admin/modules/${key}`, body);
      toast.success("Module updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  return (
    <div className="p-8 max-w-[1600px]">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="kr-overline text-[#8A52FF]">[ SUPER ADMIN · MODULES ]</div>
          <h1 className="font-display text-4xl mt-2 tracking-tight" data-testid="admin-modules-title">Module Management</h1>
          <p className="text-white/55 text-sm mt-1.5 max-w-2xl">Every AlphaCore module in one place. Publish, beta, hide, maintenance-mode — control who sees what, without redeploying.</p>
        </div>
      </div>

      <div className="mt-8 border border-white/5">
        <table className="w-full text-xs">
          <thead className="bg-[#050505] text-white/50">
            <tr>
              <th className="text-left px-3 py-2.5 font-normal">Module</th>
              <th className="text-left px-3 py-2.5 font-normal">Category</th>
              <th className="text-left px-3 py-2.5 font-normal">Status</th>
              <th className="text-left px-3 py-2.5 font-normal">Visibility</th>
              <th className="text-left px-3 py-2.5 font-normal">Version</th>
              <th className="text-right px-3 py-2.5 font-normal">Credits</th>
              <th className="text-right px-3 py-2.5 font-normal">Actions</th>
            </tr>
          </thead>
          <tbody>
            {mods.map((m) => (
              <tr key={m.key} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`admin-module-row-${m.key}`}>
                <td className="px-3 py-2.5">
                  <div className="text-white flex items-center gap-2">{m.name}</div>
                  <div className="text-[10px] text-white/40 font-mono">{m.key} · {m.description?.slice(0, 60)}</div>
                </td>
                <td className="px-3 py-2.5 text-white/70">{m.category}</td>
                <td className="px-3 py-2.5"><span className={`text-[10px] px-1.5 py-0.5 border rounded-sm uppercase font-mono ${STATUS_COLOR[m.status] || "text-white/40 border-white/20"}`}>{m.status}</span></td>
                <td className="px-3 py-2.5">
                  <Select value={m.visibility} onValueChange={(v) => patch(m.key, { visibility: v })}>
                    <SelectTrigger className="h-7 bg-[#050505] border-white/10 text-xs w-[140px]" data-testid={`admin-module-vis-${m.key}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0A0A0E] border-white/10 text-white">{VISIBILITIES.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-3 py-2.5 font-mono text-white/60">v{m.version}</td>
                <td className="px-3 py-2.5 text-right font-mono">{m.credits_required || 0}</td>
                <td className="px-3 py-2.5 text-right">
                  <div className="inline-flex gap-1">
                    <Select value={m.status} onValueChange={(v) => patch(m.key, { status: v })}>
                      <SelectTrigger className="h-7 bg-[#050505] border-white/10 text-xs w-[130px]" data-testid={`admin-module-status-${m.key}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0A0A0E] border-white/10 text-white">{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button size="sm" variant="outline" className="border-white/10 h-7" onClick={() => setEditing(m)} data-testid={`admin-module-configure-${m.key}`}>Configure</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <ConfigureDialog module={editing} onClose={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ConfigureDialog({ module, onClose }) {
  const [m, setM] = useState({ ...module });
  const set = (k, v) => setM((prev) => ({ ...prev, [k]: v }));
  const setFeature = (k, v) => setM((prev) => ({ ...prev, features: { ...(prev.features || {}), [k]: v } }));
  const save = async () => {
    try {
      await api.patch(`/admin/modules/${module.key}`, {
        name: m.name, description: m.description, category: m.category, icon: m.icon,
        status: m.status, visibility: m.visibility, order: Number(m.order || 100),
        credits_required: Number(m.credits_required || 0),
        daily_limit: Number(m.daily_limit || 0),
        upload_limit_mb: Number(m.upload_limit_mb || 0),
        storage_limit_mb: Number(m.storage_limit_mb || 0),
        version: m.version, release_notes: m.release_notes,
        features: m.features || {},
      });
      toast.success("Module saved");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const features = Object.entries(m.features || {});
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0E] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="admin-module-configure-dialog">
        <DialogHeader><DialogTitle className="font-display">Configure · {module.name}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 pt-2">
          <div className="col-span-2"><label className="kr-overline">Name</label><Input value={m.name || ""} onChange={(e) => set("name", e.target.value)} className="mt-1 bg-[#050505] border-white/10" /></div>
          <div className="col-span-2"><label className="kr-overline">Description</label><Input value={m.description || ""} onChange={(e) => set("description", e.target.value)} className="mt-1 bg-[#050505] border-white/10" /></div>
          <div><label className="kr-overline">Category</label><Input value={m.category || ""} onChange={(e) => set("category", e.target.value)} className="mt-1 bg-[#050505] border-white/10" /></div>
          <div><label className="kr-overline">Icon</label><Input value={m.icon || ""} onChange={(e) => set("icon", e.target.value)} className="mt-1 bg-[#050505] border-white/10 font-mono" /></div>
          <div><label className="kr-overline">Version</label><Input value={m.version || ""} onChange={(e) => set("version", e.target.value)} className="mt-1 bg-[#050505] border-white/10 font-mono" /></div>
          <div><label className="kr-overline">Order</label><Input type="number" value={m.order || 100} onChange={(e) => set("order", e.target.value)} className="mt-1 bg-[#050505] border-white/10 font-mono" /></div>
          <div><label className="kr-overline">Credits required</label><Input type="number" value={m.credits_required || 0} onChange={(e) => set("credits_required", e.target.value)} className="mt-1 bg-[#050505] border-white/10 font-mono" /></div>
          <div><label className="kr-overline">Daily limit (0 = unlimited)</label><Input type="number" value={m.daily_limit || 0} onChange={(e) => set("daily_limit", e.target.value)} className="mt-1 bg-[#050505] border-white/10 font-mono" /></div>
          <div><label className="kr-overline">Upload limit (MB)</label><Input type="number" value={m.upload_limit_mb || 0} onChange={(e) => set("upload_limit_mb", e.target.value)} className="mt-1 bg-[#050505] border-white/10 font-mono" /></div>
          <div><label className="kr-overline">Storage limit (MB)</label><Input type="number" value={m.storage_limit_mb || 0} onChange={(e) => set("storage_limit_mb", e.target.value)} className="mt-1 bg-[#050505] border-white/10 font-mono" /></div>
          <div className="col-span-2"><label className="kr-overline">Release notes</label><textarea rows={3} value={m.release_notes || ""} onChange={(e) => set("release_notes", e.target.value)} className="w-full mt-1 bg-[#050505] border border-white/10 text-sm p-2" /></div>
          {features.length > 0 && (
            <div className="col-span-2">
              <div className="kr-overline mb-2">FEATURE FLAGS</div>
              <div className="grid grid-cols-2 gap-2">
                {features.map(([k, v]) => (
                  <label key={k} className="flex items-center justify-between border border-white/10 p-2.5 cursor-pointer">
                    <span className="text-sm">{k.replace(/_/g, " ")}</span>
                    <button type="button" onClick={() => setFeature(k, !v)} className="text-white/70" data-testid={`admin-module-feature-${module.key}-${k}`}>
                      {v ? <ToggleRight className="h-5 w-5 text-[#00E5FF]" /> : <ToggleLeft className="h-5 w-5 text-white/30" />}
                    </button>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose} className="border-white/10">Cancel</Button><Button onClick={save} className="bg-[#8A52FF] hover:bg-[#8A52FF]/90" data-testid="admin-module-save"><Save className="h-4 w-4 mr-2" /> Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
